import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
// components
import ErrorFallback from './ErrorFallback';
// utils
import isDeployError from '../utils/isDeployError';
import { tryDeployReload } from '../utils/deployReload';

// Catches render-time errors anywhere below it (including failed React.lazy
// chunk loads from Suspense) so the app shows a friendly screen instead of a
// blank white page.
// ----------------------------------------------------------------------

export default function ErrorBoundary({ fallback, children }) {
  const handleError = (error, componentStack) => {
    if (isDeployError(error)) {
      // Stale bundle after a deploy — try one silent reload to pick up the new
      // assets. If it keeps failing, the guard stops looping and the fallback
      // ("something new went live") is shown instead.
      tryDeployReload();
    }
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, { componentStack });
  };

  const renderFallback = ({ error }) => {
    const isDeploy = isDeployError(error);

    // A local fallback keeps a crash contained to the section that broke,
    // instead of replacing the whole screen with the full-page illustration.
    if (fallback) {
      return typeof fallback === 'function' ? fallback({ isDeploy }) : fallback;
    }
    return <ErrorFallback isDeploy={isDeploy} />;
  };

  return (
    <Sentry.ErrorBoundary fallback={renderFallback} onError={handleError}>
      {children}
    </Sentry.ErrorBoundary>
  );
}

ErrorBoundary.propTypes = {
  children: PropTypes.node,
  fallback: PropTypes.oneOfType([PropTypes.node, PropTypes.func])
};
